import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  PlayCircle,
  CalendarDays,
  CheckCheck,
  Heart,
  XCircle,
  PauseCircle,
  Search,
  Bookmark,
  LayoutGrid,
  Loader2,
} from 'lucide-react';
import DoramaCard from '../components/DoramaCard';
import SkeletonCard from '../components/SkeletonCard';
import {
  useGetListsQuery,
  useGetItemsQuery,
  useGetAllItemsQuery,
} from '../services/bookmarksApi';
import styles from '../css/Bookmarks.module.css';

const SYSTEM_KEY_ORDER = [
  'watching',
  'plan_to_watch',
  'completed',
  'favorites',
  'dropped',
  'on_hold',
];

const SYSTEM_KEY_ICONS = {
  watching: <PlayCircle size={16} strokeWidth={1.5} />,
  plan_to_watch: <CalendarDays size={16} strokeWidth={1.5} />,
  completed: <CheckCheck size={16} strokeWidth={1.5} />,
  favorites: <Heart size={16} strokeWidth={1.5} />,
  dropped: <XCircle size={16} strokeWidth={1.5} />,
  on_hold: <PauseCircle size={16} strokeWidth={1.5} />,
};

const SkeletonGrid = ({ count = 24 }) => (
  <div className={styles.dramasGrid}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={i} fluid />
    ))}
  </div>
);

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Контент вкладки «Все» ────────────────────────────────────────────────────

const SearchHeader = ({ value, onChange, count, placeholder }) => (
  <div className={styles.contentToolbar}>
    <div className={styles.searchBar}>
      <Search className={styles.searchIcon} size={16} strokeWidth={1.8} />
      <input
        className={styles.searchInput}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
    <span className={styles.toolbarCount}>{count}</span>
  </div>
);

const SearchEmptyState = ({ query }) => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <Search size={32} strokeWidth={1.2} />
    </div>
    <p className={styles.emptyTitle}>Ничего не найдено</p>
    <p className={styles.emptyDescription}>По запросу «{query}» в закладках ничего нет</p>
  </div>
);

const AllContent = ({ totalItems, searchQuery }) => {
  const [page, setPage] = useState(1);
  const [allItems, setAllItems] = useState([]);
  const stateRef = useRef({ isFetching: false, hasMore: false, ready: false });
  const observerRef = useRef(null);
  const hasUserScrolledRef = useRef(false);
  const perPage = 24;
  const isSearching = searchQuery.trim().length > 0;

  const { data, isLoading, isFetching } = useGetAllItemsQuery(
    isSearching
      ? { page: 1, perPage: Math.max(totalItems, perPage, 1) }
      : { page, perPage }
  );

  const totalPages = data?.meta?.total_pages ?? 1;
  const hasMore = !isSearching && page < totalPages;

  stateRef.current = { ...stateRef.current, isFetching, hasMore };

  // Накапливаем элементы при подгрузке новых страниц
  useEffect(() => {
    if (!data?.data || isSearching) return;
    setAllItems((prev) => {
      const existingIds = new Set(prev.map((i) => i.id));
      const newItems = data.data.filter((i) => !existingIds.has(i.id));
      return [...prev, ...newItems];
    });
    // Разрешаем scroll-load только после первой успешной загрузки
    stateRef.current.ready = true;
  }, [data, isSearching]);

  // Сброс при смене режима поиска
  useEffect(() => {
    if (isSearching) return;
    setAllItems([]);
    setPage(1);
    stateRef.current.ready = false;
    hasUserScrolledRef.current = false;
  }, [isSearching]);

  useEffect(() => {
    if (isSearching) return undefined;

    const markScrolled = () => {
      hasUserScrolledRef.current = true;
    };

    window.addEventListener('wheel', markScrolled, { passive: true });
    window.addEventListener('touchmove', markScrolled, { passive: true });
    window.addEventListener('scroll', markScrolled, { passive: true });

    return () => {
      window.removeEventListener('wheel', markScrolled);
      window.removeEventListener('touchmove', markScrolled);
      window.removeEventListener('scroll', markScrolled);
    };
  }, [isSearching]);

  // ref callback — вешает observer когда sentinel появляется в DOM
  const sentinelRef = useRef((el) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting
          && hasUserScrolledRef.current
          && stateRef.current.ready
          && !stateRef.current.isFetching
          && stateRef.current.hasMore
        ) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observerRef.current.observe(el);
  }).current;

  if (isLoading && allItems.length === 0) {
    return <SkeletonGrid />;
  }

  if (totalItems === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <Bookmark size={32} strokeWidth={1.2} />
        </div>
        <p className={styles.emptyTitle}>Закладок пока нет</p>
        <p className={styles.emptyDescription}>Добавляйте дорамы из карточек или страницы просмотра</p>
      </div>
    );
  }

  const displayItems = isSearching
    ? (data?.data ?? []).filter((item) =>
        item.material?.title?.toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : allItems;

  if (isSearching && displayItems.length === 0) {
    return <SearchEmptyState query={searchQuery} />;
  }

  return (
    <>
      <div className={styles.dramasGrid}>
        {displayItems.map((item) => {
          const m = item.material;
          if (!m) return null;
          return (
            <div key={item.id} className={styles.cardWrapper}>
              <DoramaCard
                id={item.material_id}
                slug={m.slug}
                slug_id={m.slug_id}
                image={m.poster_url ? `https://doramahype.ru/uploads/posters/${m.poster_url}` : null}
                title={m.title}
                year={null}
                genres={[]}
                fluid
              />
              <p className={styles.cardDate}>{formatDate(item.added_at)}</p>
            </div>
          );
        })}
      </div>

      {!isSearching && (
        <div ref={sentinelRef} className={styles.sentinel}>
          {isFetching && <Loader2 size={20} className={styles.spinnerIcon} />}
        </div>
      )}
    </>
  );
};

// ── Контент выбранного списка ────────────────────────────────────────────────

const ListContent = ({ list, searchQuery }) => {
  const [page, setPage] = useState(1);
  const [allItems, setAllItems] = useState([]);
  const stateRef = useRef({ isFetching: false, hasMore: false, ready: false });
  const observerRef = useRef(null);
  const hasUserScrolledRef = useRef(false);
  const perPage = 24;
  const isSearching = searchQuery.trim().length > 0;

  const { data, isLoading, isFetching } = useGetItemsQuery(
    isSearching
      ? { listId: list.id, page: 1, perPage: Math.max(list.item_count ?? 0, perPage, 1) }
      : { listId: list.id, page, perPage }
  );

  const totalPages = data?.meta?.total_pages ?? 1;
  const hasMore = !isSearching && page < totalPages;

  stateRef.current = { ...stateRef.current, isFetching, hasMore };

  // Накапливаем элементы при подгрузке новых страниц
  useEffect(() => {
    if (!data?.data || isSearching) return;
    setAllItems((prev) => {
      const existingIds = new Set(prev.map((i) => i.id));
      const newItems = data.data.filter((i) => !existingIds.has(i.id));
      return [...prev, ...newItems];
    });
    stateRef.current.ready = true;
  }, [data, isSearching]);

  // Сброс при смене режима поиска
  useEffect(() => {
    if (isSearching) return;
    setAllItems([]);
    setPage(1);
    stateRef.current.ready = false;
    hasUserScrolledRef.current = false;
  }, [isSearching, list.id]);

  useEffect(() => {
    if (isSearching) return undefined;

    const markScrolled = () => {
      hasUserScrolledRef.current = true;
    };

    window.addEventListener('wheel', markScrolled, { passive: true });
    window.addEventListener('touchmove', markScrolled, { passive: true });
    window.addEventListener('scroll', markScrolled, { passive: true });

    return () => {
      window.removeEventListener('wheel', markScrolled);
      window.removeEventListener('touchmove', markScrolled);
      window.removeEventListener('scroll', markScrolled);
    };
  }, [isSearching]);

  // ref callback — вешает observer когда sentinel появляется в DOM
  const sentinelRef = useRef((el) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting
          && hasUserScrolledRef.current
          && stateRef.current.ready
          && !stateRef.current.isFetching
          && stateRef.current.hasMore
        ) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observerRef.current.observe(el);
  }).current;

  if (isLoading && allItems.length === 0) {
    return <SkeletonGrid />;
  }

  if (!isSearching && allItems.length === 0 && !isLoading) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>
          <Bookmark size={32} strokeWidth={1.2} />
        </div>
        <p className={styles.emptyTitle}>Пока пусто</p>
        <p className={styles.emptyDescription}>Добавляйте дорамы из карточек или страницы просмотра</p>
      </div>
    );
  }

  const displayItems = isSearching
    ? (data?.data ?? []).filter((item) =>
        item.material?.title?.toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : allItems;

  if (isSearching && displayItems.length === 0) {
    return <SearchEmptyState query={searchQuery} />;
  }

  return (
    <>
      <div className={styles.dramasGrid}>
        {displayItems.map((item) => {
          const m = item.material;
          if (!m) return null;
          return (
            <div key={item.id} className={styles.cardWrapper}>
              <DoramaCard
                id={item.material_id}
                slug={m.slug}
                slug_id={m.slug_id}
                image={m.poster_url ? `https://doramahype.ru/uploads/posters/${m.poster_url}` : null}
                title={m.title}
                year={null}
                genres={[]}
                fluid
              />
              <p className={styles.cardDate}>{formatDate(item.added_at)}</p>
            </div>
          );
        })}
      </div>

      {!isSearching && (
        <div ref={sentinelRef} className={styles.sentinel}>
          {isFetching && <Loader2 size={20} className={styles.spinnerIcon} />}
        </div>
      )}
    </>
  );
};

// ── Главная страница закладок ────────────────────────────────────────────────

const Bookmarks = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [activeKey, setActiveKey] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useGetListsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const lists = data?.data ?? [];
  const systemLists = SYSTEM_KEY_ORDER
    .map((key) => lists.find((l) => l.system_key === key))
    .filter(Boolean);
  const totalItems = lists.reduce((sum, l) => sum + (l.item_count ?? 0), 0);

  const activeList = activeKey === 'all' ? null : systemLists.find((l) => l.system_key === activeKey);

  // ── Гость ──
  if (!isAuthenticated) {
    return (
      <div className={styles.bookmarksPage}>
        <div className={styles.guestState}>
          <div className={styles.guestTop}>
            <div className={styles.guestBadge}>
              <Bookmark size={16} strokeWidth={1.5} />
              Закладки
            </div>

            <h1 className={styles.guestTitle}>Сохраняйте дорамы, чтобы не потерять их</h1>
            <p className={styles.guestText}>
              Эта страница собирает ваши сохранённые тайтлы в одном месте. После входа здесь появятся личные списки, и вы сможете быстро возвращаться к интересным дорамам.
            </p>

            <div className={styles.guestActions}>
              <Link to="/auth/login" className={styles.loginButton}>Войти</Link>
              <Link to="/catalog" className={styles.secondaryButton}>Открыть каталог</Link>
            </div>
          </div>

          <div className={styles.guestShelf}>
            <div className={styles.guestShelfHeader}>
              <span className={styles.guestShelfTitle}>Что дадут закладки</span>
              <span className={styles.guestShelfHint}>После входа</span>
            </div>

            <div className={styles.guestListRow}>
              <div className={styles.guestListCard}>
                <span className={`${styles.guestListDot} ${styles.guestListDotWarm}`} />
                <div className={styles.guestListMeta}>
                  <span className={styles.guestListName}>Сохранять интересные дорамы</span>
                  <span className={styles.guestListInfo}>Чтобы не искать их заново позже</span>
                </div>
              </div>
              <div className={styles.guestListCard}>
                <span className={`${styles.guestListDot} ${styles.guestListDotSoft}`} />
                <div className={styles.guestListMeta}>
                  <span className={styles.guestListName}>Быстро возвращаться к сохранённому</span>
                  <span className={styles.guestListInfo}>Все нужные тайтлы будут в одном месте</span>
                </div>
              </div>
              <div className={styles.guestListCard}>
                <span className={`${styles.guestListDot} ${styles.guestListDotCool}`} />
                <div className={styles.guestListMeta}>
                  <span className={styles.guestListName}>Держать порядок в просмотре</span>
                  <span className={styles.guestListInfo}>И не терять то, что хочется посмотреть</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.bookmarksPage}>
        <div className={styles.loadingState}>Загрузка закладок...</div>
      </div>
    );
  }

  return (
    <div className={styles.bookmarksPage}>
      <div className={styles.layout}>

        {/* ── Левая колонка — список ── */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarInner}>
            <div className={styles.sidebarCard}>
              <div className={styles.sidebarNav}>

                <button
                  className={`${styles.navItem} ${activeKey === 'all' ? styles.navItemActive : ''}`}
                  onClick={() => setActiveKey('all')}
                >
                  <span className={styles.navIcon}>
                    <LayoutGrid size={16} strokeWidth={1.5} />
                  </span>
                  <span className={styles.navName}>Все</span>
                  <span className={styles.navCount}>{totalItems}</span>
                </button>

                <div className={styles.navDivider} />

                {systemLists.map((list) => (
                  <button
                    key={list.system_key}
                    className={`${styles.navItem} ${activeKey === list.system_key ? styles.navItemActive : ''}`}
                    onClick={() => setActiveKey(list.system_key)}
                  >
                    <span className={styles.navIcon}>
                      {SYSTEM_KEY_ICONS[list.system_key]}
                    </span>
                    <span className={styles.navName}>{list.name}</span>
                    {list.item_count > 0 && (
                      <span className={styles.navCount}>{list.item_count}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Правая колонка — контент ── */}
        <main className={styles.content}>
          {activeKey === 'all' ? (
            <div className={styles.allView}>
              <SearchHeader
                value={searchQuery}
                onChange={setSearchQuery}
                count={totalItems}
                placeholder="Поиск по всем закладкам"
              />
              <AllContent totalItems={totalItems} searchQuery={searchQuery} />
            </div>
          ) : activeList ? (
            <div className={styles.listView}>
              <SearchHeader
                value={searchQuery}
                onChange={setSearchQuery}
                count={activeList.item_count ?? 0}
                placeholder={`Поиск в списке «${activeList.name}»`}
              />
              <ListContent list={activeList} searchQuery={searchQuery} />
            </div>
          ) : null}
        </main>

      </div>
    </div>
  );
};

export default Bookmarks;
